import { all, call, delay, put, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { testError as testErrorAPI } from 'spw-api';
import {
  beginAuth as beginAuthAPI,
  login as loginAPI,
  logout as logoutAPI,
  sendRecoveryEmail as sendRecoveryEmailAPI,
  sendVerifyPassword as sendVerifyPasswordAPI,
} from 'spw-api/auth';
import history from 'spw-history';
import { camelize } from 'spw-shared/utils';
import { isEqual } from 'lodash';
import { APIResponse } from 'spw-redux/types';
import {
  compareVersion,
  handleError,
  hidePermanentLogoutMessage,
} from 'spw-redux/modules/UI/messages/actions';
import { setFailedDownloadsTemplates } from 'spw-redux/modules/downloads/actions';
import * as Sentry from '@sentry/react';
import { isUrlRelative } from 'spw-components/login/utils';
import {
  loginSucceeded as loginSucceededAction,
  loginFailed as loginFailedAction,
  logoutSucceeded as logoutSucceededAction,
  loadApiTokenSucceeded as loadApiTokenSucceededAction,
  saveApiToken as saveApiTokenAction,
  setAuthLoading,
  LOGIN,
  LOGOUT,
  LOAD_API_TOKEN,
  SAVE_API_TOKEN,
  SEND_RECOVERY_EMAIL,
  SEND_VERIFY_PASSWORD,
  sendRecoveryEmailSuccess,
  sendVerifyPasswordSuccess,
  sendVerifyPasswordFailed,
  unsetUpdatePasswordSuccess,
  setEmail,
  actions,
  sendRecoveryEmailFailed,
  TEST_ERROR,
  BEGIN_AUTH,
  proceedPasswordFlow,
  SAVE_USER_TOKEN,
  saveUserToken,
} from './actions';
import { fetchUserSucceeded, fetchUser } from '../user/actions';
import { UserInitial } from '../user/types';
import { APIToken, AuthFlowType } from './types';

const LOGIN_ERROR_MESSAGE = 'Invalid user or password';

function saveApiToken(action: ReturnType<typeof actions.saveApiToken>) {
  const { token } = action.payload;
  localStorage.setItem('api_token_v2', JSON.stringify(token));
}

function saveUserTokenCall(action: ReturnType<typeof actions.saveUserToken>) {
  const { token } = action.payload;
  localStorage.setItem('user_token', token);
}

function* loadApiToken() {
  const token: APIToken = localStorage.getItem('api_token_v2')
    ? JSON.parse(localStorage.getItem('api_token_v2'))
    : null;

  if (token) {
    yield put(loadApiTokenSucceededAction(token));
  }
}

function* beginAuth(action: ReturnType<typeof actions.beginAuth>) {
  try {
    yield put(setAuthLoading(true));
    const { email } = action.payload;
    const response: APIResponse<typeof beginAuthAPI> = yield call(
      beginAuthAPI,
      email
    );
    const { flow } = response.data;
    if (flow.type === AuthFlowType.samlRedirect) {
      let timezone = 'America/Los_Angeles';
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        Sentry.captureException(e);
        // try/catch because Intl.DateTimeFormat isn't supported in some older browsers
      }
      window.location.assign(
        !isUrlRelative(flow.redirectUrl)
          ? `/${flow.redirectUrl}?target_url=/auth/saml_landing?timezone=${timezone}`
          : `${flow.redirectUrl}?target_url=/auth/saml_landing?timezone=${timezone}`
      );
    } else {
      yield put(proceedPasswordFlow());
    }
  } catch (error) {
    yield put(handleError(error));
  }
}

function* login(action: ReturnType<typeof actions.login>) {
  let blockRedirect;
  try {
    yield put(setAuthLoading(true));

    const { username, password, redirect } = action.payload;
    ({ blockRedirect } = action.payload);
    const response: AxiosResponse<typeof loginAPI> = yield call(
      loginAPI,
      username,
      password
    );

    const { apiToken, sessionExpiry, userToken } = camelize(response.data);
    const token: APIToken = {
      apiToken,
      sessionExpiry,
    };

    if (userToken) {
      yield put(saveUserToken(userToken));
    }

    if (apiToken) {
      yield put(fetchUser());
      yield put(hidePermanentLogoutMessage());
      yield put(loginSucceededAction(token));
      yield put(saveApiTokenAction(token));
      yield put(compareVersion());
      if (!blockRedirect) {
        yield call(history.replace, '/home');
      }
      if (redirect && isUrlRelative(redirect)) {
        window.location.assign(
          !redirect.startsWith('/') ? `/${redirect}` : redirect
        );
      }
      yield put(setAuthLoading(false));
    }
  } catch (error) {
    const remainingLoginAttempts = error.response?.data?.remaining;

    yield put(setAuthLoading(false));

    if (
      ['invalid_password_username', 'throttled'].includes(
        error.response?.data?.error
      )
    ) {
      yield put(
        loginFailedAction(
          remainingLoginAttempts ? LOGIN_ERROR_MESSAGE : null,
          error.response?.data?.remaining
        )
      );
    } else if (error.response?.data?.error === 'Deactivated User') {
      yield put(loginFailedAction(LOGIN_ERROR_MESSAGE, remainingLoginAttempts));
    } else {
      yield put(
        loginFailedAction(
          error?.response?.data?.error || error?.toString(),
          remainingLoginAttempts
        )
      );
      yield put(handleError(error));
    }

    if (remainingLoginAttempts === 0) history.push('/auth/lockout');
  }
}

function* logout(endSession = false) {
  try {
    if (endSession) yield call(logoutAPI);
  } catch (error) {
    yield put(handleError(error));
  } finally {
    localStorage.removeItem('api_token_v2');
    localStorage.removeItem('user_token');
    yield put(logoutSucceededAction());
    yield put(setFailedDownloadsTemplates([]));
    yield call(history.replace, '/login');
    yield put(fetchUserSucceeded(UserInitial));
  }
}

function* sendRecoveryEmail(
  action: ReturnType<typeof actions.sendRecoveryEmail>
) {
  try {
    yield call(sendRecoveryEmailAPI, action.payload);
    yield put(sendRecoveryEmailSuccess());
    history.push('/auth/recovery-email-sent');
  } catch (error) {
    if (error.response?.status === 404) {
      yield put(sendRecoveryEmailFailed(LOGIN_ERROR_MESSAGE));
    } else {
      yield put(handleError(error));
    }
  }
}

function* sendVerifyPassword(
  action: ReturnType<typeof actions.sendVerifyPassword>
) {
  try {
    const { token, newPassword, verifyPassword } = action.payload;
    if (!isEqual(newPassword, verifyPassword)) {
      throw new Error("Passwords don't match");
    }
    const { data }: AxiosResponse<{ email: string }> = yield call(
      sendVerifyPasswordAPI,
      token,
      newPassword,
      verifyPassword
    );

    yield put(sendVerifyPasswordSuccess());
    yield put(setEmail(data.email));
    history.push('/login');

    yield delay(10000);
    yield put(unsetUpdatePasswordSuccess());
  } catch (error) {
    yield put(sendVerifyPasswordFailed());
    yield put(handleError(error));
  }
}

function* testError() {
  yield call(testErrorAPI);
}

function* saga() {
  yield all([
    takeLatest(BEGIN_AUTH, beginAuth),
    takeLatest(LOGIN, login),
    takeLatest(LOGOUT, logout),
    takeLatest(SAVE_API_TOKEN, saveApiToken),
    takeLatest(SAVE_USER_TOKEN, saveUserTokenCall),
    takeLatest(LOAD_API_TOKEN, loadApiToken),
    takeLatest(SEND_RECOVERY_EMAIL, sendRecoveryEmail),
    takeLatest(SEND_VERIFY_PASSWORD, sendVerifyPassword),
    takeLatest(TEST_ERROR, testError),
  ]);
}

export default saga;
